import React, { useEffect } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { Checkbox, Form, Grid, Icon } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { useTranslation } from 'react-i18next'
import InfoPopup from '~/components/InfoPopup'
import { StyledGrid, StyledLabel } from './style'
import { CheckClimaEl } from '../../../style'
import LinkPopup from '../LinkPopup'
import { EnergyConsumptions } from '~/api/services/climaCheck'
import notify from '~/components/Notify'
import DieselPerCow from './DieselPerCow'
import EnergyPerCow from './EnergyPerCow'

interface Props {
  energyConsumptions: EnergyConsumptions
}

const CHILD_ENERGY_IDS = [4, 5, 6]
const RENEWABLE_ENERGY_ID = 3
const GREEN_ELECTRICITY_ID = 2

const EnergyFields: React.FC<Props> = ({ energyConsumptions }) => {
  const { t } = useTranslation()
  const { control, getValues, formState: { isSubmitted, errors } } = useFormContext()

  const { fields, append, update, replace } = useFieldArray({
    control,
    name: 'cowsEnergyConsumptions',
    keyName: 'key'
  })

  useEffect(() => {
    const isMainEnergyError = checkPercentage()
    const isRenewableEnergyError = Boolean(fields.find(field => field['id'] === RENEWABLE_ENERGY_ID)) && checkPercentageChild()
    if (errors?.cowsEnergyConsumptions && isMainEnergyError) {
      notify('Die Zusammenssetzung des Energieeinsatzes muss insgesamt 100% betragen.', 'error')
    }
    if (errors?.cowsEnergyConsumptions && isRenewableEnergyError) {
      notify('Die Zusammenssetzung des Stroms aus Eigenerzeugung muss insgesamt 100% betragen.', 'error')
    }
  }, [errors?.cowsEnergyConsumptions])

  function handleChecked(field, checked) {
    if (checked) {
      append({
        ...field,
        percent: ''
      })
      return
    }
    const index = fields.findIndex(fieldEnergie => fieldEnergie['id'] === field.id)
    const newFields = index >= 0 ? fields.filter(field => field['id'] !== fields[index]['id']) : fields
    // remove children
    const filteredChildrenFields = index >= 0 ? newFields.filter(field => field['parentId'] !== fields[index]['id']) : newFields
    if (index >= 0) {
      replace(filteredChildrenFields)
    }
  }

  function checkPercentage() {
    const energyTypes = getValues('cowsEnergyConsumptions').filter(value => !CHILD_ENERGY_IDS.find(id => id === value.id))
    return energyTypes.reduce((previousValue, currentValue) => {
      return previousValue + parseFloat(currentValue.percent.replace(',', '.'))
    }, 0) !== 100
  }

  function checkPercentageChild() {
    const childEnergyTypes = getValues('cowsEnergyConsumptions').filter(value => CHILD_ENERGY_IDS.find(id => id === value.id))
    return childEnergyTypes.reduce((previousValue, currentValue) => {
      return previousValue + parseFloat(currentValue.percent.replace(',', '.'))
    }, 0) !== 100
  }

  function renderShareRenewableEnergie() {
    return fields.reduce((previousValue, currentValue) => {
      const updateValue = currentValue['id'] === RENEWABLE_ENERGY_ID || currentValue['id'] === GREEN_ELECTRICITY_ID ?
        parseFloat(currentValue['percent']?.replace(',', '.') || 0) : 0
      return previousValue + updateValue
    }, 0)
  }

  function renderEnergyConsumption() {

    return <Form.Group widths={'equal'} style={{ gap: 20 }}>
      <Form.Field>
        <Form.Group widths={'equal'}>
          <FormFields.Input
            control={control}
            typeInput='controled'
            rules={{
              min: 0,
              max: 100000,
              required: true
            }}
            suffix={' Liter pro Jahr'}
            noErrorMessage
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <label>{t('Diesel consumption (l/Kuh u. Jahr)')}</label>
              <InfoPopup
                content={`Hier zählt der direkte Verbrauch für die Fütterung (Mischen der Kuhfutterration) sowie für das Misten/Einstreuen. 
                  Außenwirtschaftliche Tätigkeiten wie Häckseln und Düngen gehören nicht dazu. Der Dieselverbrauch für das Jungvieh wird separat abgefragt.`}
                styleIcon={{ fontSize: '1.2em' }}
                position={'top center'}
              />
              <LinkPopup
                content={`Stromabrechnung, bei Eigenstromerzeugung Stromzählerstände; Falls kein eigener Zähler für den Kuhstall vorhanden ist, 
                  Abzug für Haushalt (ca. 1.000 kWh je Person) und sonstige Verbrauchsstellen, wie Färsen etc. vornehmen.`}
              />
            </div>}
            withoutAsterisk
            name={'dieselConsumption'}
            placeholder={'Liter pro Jahr'}
          />
          <DieselPerCow />
        </Form.Group>
      </Form.Field>

      <Form.Field>
        <Form.Group widths={'equal'}>
          <FormFields.Input
            control={control}
            name={'energyUse'}
            suffix={' kWh'}
            withoutAsterisk
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <label>{'Energieeinsatz/Stromverbrauch'}</label>
              <InfoPopup
                content={`Wie viel Strom haben Sie im Milchkuhstall verbraucht? Hier sind melken, kühlen, Lampen, etc. gemeint. 
                Als Faustzahl gibt das KTBL eine Spanne von 2340 bis 4090  kWh/Jahr für einen Betrieb mit 100 Kühen ohne AMS und Weidegang an. Der Stromverbrauch in der Färsenaufzucht wird separat abgefragt.`}
                position={'top center'}
                styleIcon={{ fontSize: '1.2em' }}
              />
              <LinkPopup
                content={'Stromabrechnung, bei Eigenstromerzeugung Stromzählerstände'}
              />
            </div>}
            typeInput='controled'
            noErrorMessage
            rules={{
              required: true
            }}
            placeholder={'kWh pro Jahr'}
          />
          <EnergyPerCow />
        </Form.Group>
      </Form.Field>

    </Form.Group>
  }

  function renderEnergyInput(option, error) {
    const fieldIndex = fields.findIndex(field => field['id'] === option.id)
    const checkedEnergy = fieldIndex >= 0

    return <>
      <Grid.Row
        key={option.id}>
        <Checkbox
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{option.label}</div>
            {option?.info && <InfoPopup
              content={option.info}
              position={'top center'}
              styleIcon={{ fontSize: '1.2em' }}
            />}
            {option?.dataSource && <LinkPopup
              content={option?.dataSource?.replace('Datenquelle: ', '')}
            />}
          </label>}
          disabled={false}
          checked={Boolean(fields.find(energyField => energyField['id'] === option.id))}
          onChange={(e, data) => handleChecked(option, data.checked)}
          style={{ marginBottom: '0.5em' }}
        />
        {checkedEnergy ? <FormFields.Input
          control={control}
          styleLabel={{ marginBottom: 0 }}
          error={isSubmitted && error}
          typeInput={'controled'}
          suffix={' %'}
          noErrorMessage
          withoutAsterisk
          rules={{
            required: true
          }}
          onChange={(event) => {
            update(fieldIndex, {
              ...fields[fieldIndex],
              percent: event.target.value
            })
          }}
          styleControlled={{ pointerEvents: checkedEnergy ? 'all' : 'none', opacity: checkedEnergy ? 1 : '0.6' }}
          name={`cowsEnergyConsumptions[${fieldIndex}].percent`}
          placeholder={t('in %')}
        /> : <Form.Input error={checkedEnergy && isSubmitted && error && Boolean(option.parentId)}
          style={{ pointerEvents: checkedEnergy ? 'all' : 'none', opacity: checkedEnergy ? 1 : '0.6' }}
          placeholder={t('in %')} />}
      </Grid.Row>
      {option.children?.length > 0 && checkedEnergy && <div style={{ padding: '1em', border: '1px solid rgba(34,36,38,.15)', borderRadius: '.28571429rem' }}>
        <div>
          {option.children.map((option) => {
            return renderEnergyInput(option, checkPercentageChild())
          })}
        </div>
      </div>
      }
    </>
  }

  function handleFormValidate() {
    if (fields.find(energy => energy['id'] === RENEWABLE_ENERGY_ID)) {
      return !checkPercentage() && !checkPercentageChild()
    }
    return !checkPercentage()
  }

  return <Form.Field>
    <label style={{ fontSize: '15px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div>{'Energieeinsatz'}</div>
        <InfoPopup
          content={t('Please select all the types from which you receive your electricity. Then distribute the percentage share among the selected options. In total these must add up to 100%.')}
          styleIcon={{ fontSize: '1.2em' }}
          position={'top center'}
        />
      </div>
    </label>
    <CheckClimaEl.StyledDivider />
    <div style={{ width: 'fit-content', marginBottom: '2em', marginTop: '1em' }}>
      <div>{'Kraftstoff- und Energieverbrauch '}</div>
      <CheckClimaEl.StyledDivider />
    </div>
    {renderEnergyConsumption()}
    <StyledGrid columns={4} style={{ marginTop: '1em' }}>
      <Controller
        name={'cowsEnergyConsumptions'}
        control={control}
        rules={{
          validate: () => handleFormValidate()
        }}
        render={({ fieldState }) => {
          const error = fieldState.error ? checkPercentage() : false
          return <>
            {energyConsumptions.map((option, index) => {
              return <Grid.Column key={`energyCow-${index}`}>
                {renderEnergyInput(option, error)}
              </Grid.Column>
            })}
            <Grid.Column>
              <Form.Field
                style={{
                  padding: 9,
                  borderRadius: 5,
                  marginTop: 15,
                  display: 'flex',
                  gap: 5,
                  alignItems: 'center'
                }}>
                <Icon name='leaf' size='big' color='green' />
                <div>
                  <StyledLabel>
                    <div>{'Anteil Erneuerbare Energie'}</div>
                  </StyledLabel>
                  <div className='percent'>{`${renderShareRenewableEnergie()} %`}</div>
                </div>
              </Form.Field>
            </Grid.Column>
          </>
        }}
      />
    </StyledGrid>
  </Form.Field>
}
export default EnergyFields